import { RiskEpiDeliveryDTO } from "../../types/RiskAssessmentDTO";

export type DeliverySortColumn = 'date' | 'sector' | 'position' | 'epi' | 'ca' | 'employees';
export type DeliveryField = 'sector' | 'position';

export interface DeliverySort {
	column: DeliverySortColumn;
	direction: 'asc' | 'desc';
}

export interface DeliveryGroup {
	date: string;
	items: RiskEpiDeliveryDTO[];
}

export interface DeliveryPeriod {
	months: number | null;
	label: string;
}

export const DELIVERY_PERIODS: DeliveryPeriod[] = [
	{ months: 1, label: 'Último mês' },
	{ months: 3, label: 'Últimos 3 meses' },
	{ months: 6, label: 'Últimos 6 meses' },
	{ months: 12, label: 'Último ano' },
	{ months: null, label: 'Todo período' }
];

export const DEFAULT_DELIVERY_PERIOD = DELIVERY_PERIODS[0];

export const DEFAULT_DELIVERY_SORT: DeliverySort = { column: 'date', direction: 'desc' };

function compareText(first: string, second: string): number {
	return (first || '').localeCompare(second || '', 'pt-BR', { sensitivity: 'base', numeric: true });
}

function compareDefault(first: RiskEpiDeliveryDTO, second: RiskEpiDeliveryDTO): number {
	return compareText(first.sector, second.sector) || compareText(first.position, second.position)
		|| compareText(first.epi, second.epi) || compareText(first.ca, second.ca);
}

function compareBy(column: DeliverySortColumn, first: RiskEpiDeliveryDTO, second: RiskEpiDeliveryDTO): number {
	if (column === 'employees') {
		return first.employees - second.employees;
	}
	if (column === 'date') {
		return 0;
	}
	return compareText(first[column], second[column]);
}

export function filterDeliveries(deliveries: RiskEpiDeliveryDTO[], sector: string | null,
		position: string | null): RiskEpiDeliveryDTO[] {
	return deliveries.filter(item => (sector === null || item.sector === sector)
		&& (position === null || item.position === position));
}

export function deliveryOptions(deliveries: RiskEpiDeliveryDTO[], field: DeliveryField, sector: string | null): string[] {
	return Array.from(new Set(deliveries
		.filter(item => field === 'sector' || sector === null || item.sector === sector)
		.map(item => item[field])))
		.sort(compareText);
}

export function groupDeliveries(deliveries: RiskEpiDeliveryDTO[], sort: DeliverySort): DeliveryGroup[] {
	const byDate = new Map<string, RiskEpiDeliveryDTO[]>();
	deliveries.forEach(item => {
		const items = byDate.get(item.date);
		if (items) {
			items.push(item);
		} else {
			byDate.set(item.date, [item]);
		}
	});
	const dateFactor = sort.column === 'date' && sort.direction === 'asc' ? 1 : -1;
	const factor = sort.direction === 'asc' ? 1 : -1;
	return Array.from(byDate.entries())
		.sort(([first], [second]) => dateFactor * first.localeCompare(second))
		.map(([date, items]) => ({
			date,
			items: [...items].sort((first, second) => factor * compareBy(sort.column, first, second)
				|| compareDefault(first, second))
		}));
}

export function toggleDeliverySort(previous: DeliverySort, column: DeliverySortColumn): DeliverySort {
	if (previous.column === column) {
		return { column, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
	}
	return { column, direction: column === 'date' || column === 'employees' ? 'desc' : 'asc' };
}

export function formatDeliveryDate(date: string): string {
	const [year, month, day] = (date || '').split('-');
	return day && month && year ? `${day}/${month}/${year}` : date;
}
